import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import Icon from 'components/AppIcon';
import MainSidebar from 'components/MainSidebar';
import QuestCard from './components/QuestCard';
import AchievementCard from './components/AchievementCard';
import SeasonPanel from './components/SeasonPanel';
import { SkeletonCard, EmptyState } from 'components/SkeletonLoader';
import { useQuestPageRealtime } from '../../hooks/useRealtimeQuests';
import { useAuth } from '../../contexts/AuthContext';
import { useToast, TOAST_TYPE } from '../../hooks/useToast';

const QUEST_TABS = ['ALL', 'DAILY', 'WEEKLY', 'SEASONAL'];
const ACH_TABS = ['ALL', 'TRADING_MASTER', 'BUSINESS_TYCOON', 'CRIME_KINGPIN', 'FACTION_LEGEND', 'WEALTH_MILESTONE'];

// Achievement Unlock Popup
const AchievementUnlockPopup = ({ achievement, playerAchievement, onDismiss }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (achievement) {
      setTimeout(() => setVisible(true), 50);
      const t = setTimeout(() => {
        setVisible(false);
        setTimeout(onDismiss, 400);
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [achievement]);

  if (!achievement) return null;

  return (
    <div
      className="fixed inset-0 z-[9990] flex items-center justify-center pointer-events-none"
      style={{ padding: '1rem' }}
    >
      <div
        className="pointer-events-auto bg-gradient-to-br from-yellow-900/95 to-orange-900/95 border border-yellow-500/50 rounded-2xl p-6 max-w-sm w-full shadow-2xl"
        style={{
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(20px)',
          opacity: visible ? 1 : 0,
          transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s ease',
          boxShadow: '0 0 40px rgba(234,179,8,0.3)',
        }}
      >
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-yellow-500/20 border-2 border-yellow-500/50 flex items-center justify-center mx-auto mb-3">
            <Icon name={achievement?.icon || 'Trophy'} size={28} className="text-yellow-400" />
          </div>
          <p className="text-xs text-yellow-400/70 uppercase tracking-widest font-medium mb-1">Achievement Unlocked</p>
          <h3 className="font-bold text-xl text-yellow-300 mb-1">{achievement?.achievement_name}</h3>
          <p className="text-sm text-yellow-200/70">{achievement?.description}</p>
          {playerAchievement?.tier && (
            <span className="inline-block mt-2 px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-xs font-bold border border-yellow-500/30">
              {playerAchievement?.tier} Tier
            </span>
          )}
        </div>
        <button
          onClick={() => { setVisible(false); setTimeout(onDismiss, 300); }}
          className="mt-4 w-full py-2 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 text-sm font-medium transition-colors"
        >
          Awesome!
        </button>
      </div>
    </div>
  );
};

const QuestsAchievements = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('quests');
  const [questFilter, setQuestFilter] = useState('ALL');
  const [achFilter, setAchFilter] = useState('ALL');
  const [quests, setQuests] = useState([]);
  const [playerQuests, setPlayerQuests] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [achievementTiers, setAchievementTiers] = useState([]);
  const [playerAchievements, setPlayerAchievements] = useState([]);
  const [season, setSeason] = useState(null);
  const [seasonEvents, setSeasonEvents] = useState([]);
  const [seasonLeaderboard, setSeasonLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [popupAchievement, setPopupAchievement] = useState(null);
  const [popupPlayerAch, setPopupPlayerAch] = useState(null);
  const [localTierUpData, setLocalTierUpData] = useState(null);

  // Real-time toast handler
  const handleRealtimeToast = useCallback((type, data) => {
    if (type === 'quest_complete') {
      showToast(
        `Quest Completed! Earned ${data?.reward_xp || 500} XP`,
        TOAST_TYPE?.SUCCESS,
        { duration: 6000, title: '🎯 Quest Complete' }
      );
    } else if (type === 'achievement_unlock') {
      showToast(
        `Achievement Unlocked: ${data?.achievement_name || 'New Achievement'}`,
        TOAST_TYPE?.SUCCESS,
        { duration: 7000, title: '🏆 Achievement' }
      );
    } else if (type === 'tier_up') {
      showToast(
        `Tier Up! You reached ${data?.progress?.current_tier} tier!`,
        TOAST_TYPE?.SUCCESS,
        { duration: 8000, title: '⭐ Tier Up!' }
      );
    } else if (type === 'weekly_bonus') {
      showToast(
        'Weekly bonus available! Claim your reward.',
        TOAST_TYPE?.INFO,
        { duration: 10000, title: '🎁 Weekly Bonus' }
      );
    } else if (type === 'rank_change') {
      const { diff } = data;
      if (diff > 0) {
        showToast(
          `Leaderboard: You moved up ${diff} rank${diff > 1 ? 's' : ''}!`,
          TOAST_TYPE?.SUCCESS,
          { duration: 5000 }
        );
      }
    }
  }, [showToast]);

  const {
    realtimePlayerQuests,
    realtimePlayerAchievements,
    realtimeSeasonProgress,
    completedQuestId,
    unlockedAchievement,
    tierUpData,
    rankChange,
  } = useQuestPageRealtime({ userId: user?.id, onToast: handleRealtimeToast });

  // Show achievement popup when unlocked
  useEffect(() => {
    if (unlockedAchievement) {
      const ach = achievements?.find(a => a?.id === unlockedAchievement?.achievement_id);
      if (ach) {
        setPopupAchievement(ach);
        setPopupPlayerAch(unlockedAchievement);
      }
    }
  }, [unlockedAchievement, achievements]);

  // Sync tier up data
  useEffect(() => {
    if (tierUpData) setLocalTierUpData(tierUpData);
  }, [tierUpData]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user: authUser } } = await supabase?.auth?.getUser();

      const [questsRes, playerQuestsRes, achRes, tierRes, playerAchRes, seasonRes] =
        await Promise.all([
          supabase?.from('quests')?.select('*')?.eq('is_active', true)?.order('quest_type'),
          authUser
            ? supabase?.from('player_quests')?.select('*')?.eq('user_id', authUser?.id)
            : Promise.resolve({ data: [] }),
          supabase?.from('achievements')?.select('*')?.eq('is_active', true),
          supabase?.from('achievement_tiers')?.select('*'),
          authUser
            ? supabase?.from('player_achievements')?.select('*')?.eq('user_id', authUser?.id)
            : Promise.resolve({ data: [] }),
          supabase?.from('seasons')?.select('*')?.eq('is_active', true)?.single(),
        ]);

      setQuests(questsRes?.data || []);
      setPlayerQuests(playerQuestsRes?.data || []);
      setAchievements(achRes?.data || []);
      setAchievementTiers(tierRes?.data || []);
      setPlayerAchievements(playerAchRes?.data || []);

      const activeSeason = (!seasonRes?.error || seasonRes?.error?.code === 'PGRST116') ? seasonRes?.data : null;
      setSeason(activeSeason);

      if (activeSeason) {
        const [eventsRes, lbRes] = await Promise.all([
          supabase?.from('season_events')?.select('*')?.eq('season_id', activeSeason?.id)?.eq('is_active', true),
          supabase?.from('season_leaderboards')?.select('*, user_profiles(username)')?.eq('season_number', activeSeason?.season_number)?.order('final_net_worth', { ascending: false })?.limit(10),
        ]);
        setSeasonEvents(eventsRes?.data || []);
        setSeasonLeaderboard(lbRes?.data || []);

        // Load initial season progress
        if (authUser) {
          const { data: spData } = await supabase
            ?.from('season_player_progress')
            ?.select('*')
            ?.eq('user_id', authUser?.id)
            ?.eq('season_id', activeSeason?.id)
            ?.single();
          if (spData) {
            // Trigger initial season progress display via realtime state
          }
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClaimReward = async (playerQuestId) => {
    setClaimingId(playerQuestId);
    try {
      const { error: claimErr } = await supabase?.rpc('claim_quest_reward', {
        p_player_quest_id: playerQuestId,
      });
      if (claimErr) throw claimErr;
      await fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to claim reward');
    } finally {
      setClaimingId(null);
    }
  };

  // Merge real-time updates with fetched data
  const getMergedPlayerQuest = (questId) => {
    const rt = realtimePlayerQuests?.[questId];
    const fetched = playerQuests?.find((pq) => pq?.quest_id === questId);
    return rt || fetched;
  };

  const getMergedPlayerAchievement = (achId) => {
    const rt = realtimePlayerAchievements?.[achId];
    const fetched = playerAchievements?.find((pa) => pa?.achievement_id === achId);
    return rt || fetched;
  };

  const filteredQuests = quests?.filter(
    (q) => questFilter === 'ALL' || q?.quest_type === questFilter
  );

  const filteredAchievements = achievements?.filter(
    (a) => achFilter === 'ALL' || a?.achievement_type === achFilter
  );

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <MainSidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(p => !p)}
      />

      {/* Achievement Unlock Popup */}
      <AchievementUnlockPopup
        achievement={popupAchievement}
        playerAchievement={popupPlayerAch}
        onDismiss={() => { setPopupAchievement(null); setPopupPlayerAch(null); }}
      />

      <main className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-6xl mx-auto">
          <div className="mb-6">
            <h1 className="text-h2 font-heading text-foreground flex items-center gap-3">
              <Icon name="Target" size={28} color="var(--color-primary)" />
              Quests & Achievements
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Complete quests and unlock achievements to earn rewards</p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/30 flex items-center gap-2">
              <Icon name="AlertCircle" size={14} color="var(--color-error)" />
              <span className="text-sm text-error">{error}</span>
              <button onClick={fetchData} className="ml-auto text-xs text-primary underline">Retry</button>
            </div>
          )}

          {/* Season Panel */}
          {season && (
            <SeasonPanel
              season={season}
              events={seasonEvents}
              leaderboard={seasonLeaderboard}
              seasonProgress={realtimeSeasonProgress}
              tierUpData={localTierUpData}
              rankChange={rankChange}
              onDismissTierUp={() => setLocalTierUpData(null)}
            />
          )}

          {/* Tabs */}
          <div className="flex gap-1 mb-6 mt-6 bg-surface rounded-lg p-1 w-fit">
            {['quests', 'achievements']?.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2 rounded-md text-sm font-medium transition-all capitalize ${
                  activeTab === tab ? 'bg-primary text-zinc-950' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {activeTab === 'quests' && (
            <div>
              <div className="flex gap-1 mb-4 overflow-x-auto pb-1">
                {QUEST_TABS?.map(f => (
                  <button
                    key={f}
                    onClick={() => setQuestFilter(f)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                      questFilter === f ? 'bg-primary/20 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground border border-transparent'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array(6)?.fill(0)?.map((_, i) => <SkeletonCard key={i} />)}
                </div>
              ) : filteredQuests?.length === 0 ? (
                <EmptyState
                  icon={<Icon name="Target" size={28} color="var(--color-primary)" />}
                  title="No active quests"
                  description="Check back tomorrow for new challenges. Daily and weekly quests refresh regularly!"
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredQuests?.map(quest => (
                    <QuestCard
                      key={quest?.id}
                      quest={quest}
                      playerQuest={getMergedPlayerQuest(quest?.id)}
                      onClaim={handleClaimReward}
                      claiming={claimingId}
                      loading={false}
                      isJustCompleted={completedQuestId === quest?.id}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'achievements' && (
            <div>
              <div className="flex gap-1 mb-4 overflow-x-auto pb-1">
                {ACH_TABS?.map(f => (
                  <button
                    key={f}
                    onClick={() => setAchFilter(f)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                      achFilter === f ? 'bg-primary/20 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground border border-transparent'
                    }`}
                  >
                    {f?.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array(6)?.fill(0)?.map((_, i) => <SkeletonCard key={i} />)}
                </div>
              ) : filteredAchievements?.length === 0 ? (
                <EmptyState
                  icon={<Icon name="Award" size={28} color="var(--color-warning)" />}
                  title="No achievements in this category"
                  description="Complete quests and in-game activities to unlock achievements and earn rewards."
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredAchievements?.map(ach => (
                    <AchievementCard
                      key={ach?.id}
                      achievement={ach}
                      tiers={achievementTiers?.filter(t => t?.achievement_id === ach?.id)}
                      playerAchievement={getMergedPlayerAchievement(ach?.id)}
                      isJustUnlocked={unlockedAchievement?.achievement_id === ach?.id}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default QuestsAchievements;
